import React, { useCallback, useEffect, useState } from "react"
import { FieldArray, useField } from "formik"
import { useTranslation } from "react-i18next"
import { Form } from "react-bootstrap"
import cn from "classnames"
import styles from "../../../CreatableTagSelect/CreatableTagSelect.module.scss"
import { CreatableSelect } from "../../../Select/Select"
import { formTranslation } from "../../../../locales/form"
import Tag from "../../../Tag/Tag"
import { TCreatableSelectProps, TOptionItem } from "../../../CreatableTagSelect/CreatableTagSelect"
import { usePaginationSelect } from "../helpers/usePaginationSelect"
import { CreatableSelectProps } from "../../../Select/Select.types"
import { GetOptionsType } from "../helpers/types"
import { parseFromVisitorFields } from "../../../../utility/channels/slotsParser"
import Skeleton from "../../../Skeleton/Skeleton"
import MenuList from "../MenuList/MenuList"
import SearchIndicatorsContainer from "../SearchIndicatorsContainer/SearchIndicatorsContainer"

export interface PaginationMultiSelectBaseProps extends Omit<CreatableSelectProps, "options"> {
    getOptions: GetOptionsType
    getInitialLabel: (value: string[]) => Promise<Record<string, string>>
    isTitlesLoading: boolean
}

const PaginationMultiSelectBase: React.FC<PaginationMultiSelectBaseProps> = props => {
    const {
        id,
        name,
        label,
        placeholder,
        placeholderOnAnySelected,
        disableOnAllSelected,
        nonCreatable,
        TagProps,
        className,
        disabled,
        icon,
        placement,
        getOptions,
        getInitialLabel,
        isTitlesLoading,
        ...restProps
    } = props
    const { options, inputValue, handleInputChange, handleOnMenuScrollToBottom, handleOnMenuClose, handleOnMenuOpen } =
        usePaginationSelect(getOptions)

    const [field] = useField(name)
    const hasOptions = Boolean(options.length)
    const defaultCSelectProps: TCreatableSelectProps = []
    const { t } = useTranslation()
    const [labels, setLabels] = useState<Record<string, string>>({})

    // Загрузка начального заголовка, если в поле формы было заполнено значение
    useEffect(() => {
        if (field.value) {
            getInitialLabel(field.value).then(labels => {
                setLabels(labels)
            })
        }
    }, [])

    const handleOnChange = useCallback(
        (option: TOptionItem, pushFn: (obj: unknown) => void) => {
            if (!option) {
                return
            }

            if (hasOptions && field.value.includes(option.value)) {
                return
            }

            pushFn(option.value)
        },
        [field, hasOptions]
    )

    let defaultValue = { label: placeholder, value: "" }

    if (placeholderOnAnySelected && field.value.length) {
        defaultValue = { label: placeholderOnAnySelected, value: "" }
    }

    if (nonCreatable) {
        defaultCSelectProps["isValidNewOption"] = () => false
    }

    if (disableOnAllSelected) {
        defaultCSelectProps["isDisabled"] = !options.length
    }

    if (disabled) {
        defaultCSelectProps["isDisabled"] = disabled
    }

    if (placement) {
        defaultCSelectProps["menuPlacement"] = placement
    }

    const renderTags = (isTitlesLoading: boolean, remove: (index: number) => void) => {
        if (isTitlesLoading) {
            return <Skeleton className={styles.creatableTagSelect__tags__skeleton} />
        }

        const handelDelete = (index: number) => {
            if (disabled) {
                return
            }
            remove(index)
        }

        return field.value.map((value: string, index: number) => {
            const parsedKey = parseFromVisitorFields(value)
            const title = labels[parsedKey] ? labels[parsedKey] : value

            return (
                <Tag
                    {...TagProps}
                    disabled={disabled}
                    className={cn(styles.creatableTagSelect__tag, TagProps?.className)}
                    key={`${value}-${index}`}
                    index={index}
                    title={title}
                    onDelete={() => handelDelete(index)}
                    testId={id + `${index}`}
                />
            )
        })
    }

    return (
        <div data-testid={id}>
            <FieldArray
                name={name}
                render={({ push, remove }) => (
                    <>
                        <Form.Group controlId={id} className={cn(styles.creatableTagSelect, className)}>
                            {label && (
                                <Form.Label>
                                    {label} {icon}
                                </Form.Label>
                            )}
                            <CreatableSelect
                                {...defaultCSelectProps}
                                className={cn(
                                    styles.creatableTagSelect__tagInput,
                                    hasOptions && styles.creatableTagSelect__tagInput_withArrow
                                )}
                                value={defaultValue}
                                options={options}
                                noOptionsMessage={() => null}
                                onChange={option => handleOnChange(option, push)}
                                formatCreateLabel={(inputValue: string) => `${t(formTranslation.add)}: ${inputValue}`}
                                inputValue={inputValue}
                                onInputChange={handleInputChange}
                                onMenuScrollToBottom={handleOnMenuScrollToBottom}
                                onMenuClose={handleOnMenuClose}
                                onMenuOpen={handleOnMenuOpen}
                                filterOption={() => true}
                                components={{
                                    MenuList,
                                    IndicatorsContainer: SearchIndicatorsContainer
                                }}
                                {...restProps}
                            />
                            {Boolean(field.value.length) && (
                                <div className={styles.creatableTagSelect__tags}>
                                    {renderTags(isTitlesLoading, remove)}
                                </div>
                            )}
                        </Form.Group>
                    </>
                )}
            />
        </div>
    )
}

export default PaginationMultiSelectBase
